import {
  COLORS,
  CONTENT_WIDTH,
  MARGIN,
  PdfDocument,
  drawReportHeader,
  wrapText,
} from '@/lib/reporting/pdfKit';
import { documentTypeLabel } from '@/lib/documentSource';
import {
  assignJournalBilag,
  journalMarkdownToPlainText,
  type JournalBilagItem,
  type JournalExportAmendment,
  type JournalExportAmendmentKind,
  type JournalExportAttachment,
  type JournalExportDocument,
  type JournalExportEntry,
  type JournalExportStatus,
} from './journalExportData';

export interface JournalPdfLabels {
  brand: string;
  title: string;
  patient: string;
  patientId: string;
  clinic: string;
  exported: string;
  attachments: string;
  correction: string;
  addendum: string;
  finalized: string;
  draft: string;
  page: string;
  pageOf: string;
  footer: string;
  empty: string;
  bilag: string;
  bilagSection: string;
  originalFile: string;
  filename: string;
  fileType: string;
  date: string;
  uploadedBy: string;
  relatedEntry: string;
}

export const EN_JOURNAL_PDF_LABELS: JournalPdfLabels = {
  brand: 'Clinio',
  title: 'Patient journal',
  patient: 'Patient',
  patientId: 'Patient ID',
  clinic: 'Clinic',
  exported: 'Exported',
  attachments: 'Attachments',
  correction: 'Correction',
  addendum: 'Addendum',
  finalized: 'Finalized',
  draft: 'Draft',
  page: 'Page',
  pageOf: 'of',
  footer: 'Patient journal',
  empty: 'No journal entries.',
  bilag: 'Bilag',
  bilagSection: 'BILAG',
  originalFile: 'Original file',
  filename: 'Filename',
  fileType: 'File type',
  date: 'Date',
  uploadedBy: 'Uploaded by',
  relatedEntry: 'Related journal entry',
};

export interface JournalPdfOptions {
  locale?: string;
  labels?: JournalPdfLabels;
  generatedAt?: Date;
  includeBilag?: boolean;
  embeddedByDocumentId?: Readonly<Record<string, Uint8Array>>;
}

const BODY_SIZE = 10;
const BODY_LEADING = 13;
const META_SIZE = 9;
const META_LEADING = 12;
const DATE_SIZE = 11;
const DATE_LEADING = 15;
const AMENDMENT_INDENT = 16;

function wrapMultiline(text: string, maxWidth: number, fontSize: number, bold = false): string[] {
  const paragraphs = text.replace(/\r\n/g, '\n').split('\n');
  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    if (paragraph.trim() === '') {
      lines.push('');
      continue;
    }
    lines.push(...wrapText(paragraph, maxWidth, fontSize, bold));
  }
  return lines;
}

function formatDate(value: string, locale: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function formatTime(value: string, locale: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDateTime(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function entryHeading(entry: JournalExportEntry, locale: string): string {
  const dateSource = entry.appointmentAt || entry.encounterAt || entry.clinicalDate;
  const dateLabel = formatDate(dateSource, locale);
  const timeSource = entry.appointmentAt || entry.encounterAt;
  const timeLabel = timeSource ? formatTime(timeSource, locale) : null;
  return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
}

function statusLabel(status: JournalExportStatus, labels: JournalPdfLabels): string {
  switch (status) {
    case 'finalized':
      return labels.finalized;
    case 'draft':
      return labels.draft;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function amendmentKindLabel(kind: JournalExportAmendmentKind, labels: JournalPdfLabels): string {
  switch (kind) {
    case 'correction':
      return labels.correction;
    case 'addendum':
      return labels.addendum;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function drawWrapped(
  doc: PdfDocument,
  lines: string[],
  options: {
    size: number;
    leading: number;
    bold?: boolean;
    color?: (typeof COLORS)[keyof typeof COLORS];
    x?: number;
    maxWidth?: number;
  },
) {
  for (const line of lines) {
    if (line === '') {
      doc.gap(options.leading * 0.45);
      continue;
    }
    doc.flowLine(line, {
      size: options.size,
      leading: options.leading,
      bold: options.bold,
      color: options.color,
      x: options.x,
      maxWidth: options.maxWidth,
    });
  }
}

function bilagTitle(number: number, labels: JournalPdfLabels): string {
  return `${labels.bilag} ${number}`;
}

function attachmentMetaLines(
  attachment: JournalExportAttachment,
  labels: JournalPdfLabels,
  locale: string,
  options: { includeRelated?: { date: string | null; service: string | null } } = {},
): string[] {
  const lines: string[] = [];
  if (attachment.notes) lines.push(attachment.notes);
  lines.push(`${labels.filename}: ${attachment.filename}`);
  const kind = documentTypeLabel(attachment.mimeType);
  if (kind) lines.push(`${labels.fileType}: ${kind}`);
  const when = attachment.uploadedAt || attachment.createdAt;
  if (when) lines.push(`${labels.date}: ${formatDate(when, locale)}`);
  if (attachment.uploadedBy) lines.push(`${labels.uploadedBy}: ${attachment.uploadedBy}`);
  if (options.includeRelated) {
    const relatedParts = [
      options.includeRelated.date ? formatDate(options.includeRelated.date, locale) : null,
      options.includeRelated.service,
    ].filter((part): part is string => Boolean(part));
    if (relatedParts.length > 0) {
      lines.push(`${labels.relatedEntry}: ${relatedParts.join(' · ')}`);
    }
  }
  if (attachment.zipFilename) {
    lines.push(`${labels.originalFile}: ${attachment.zipFilename}`);
  }
  return lines;
}

function drawBilagReference(
  doc: PdfDocument,
  attachment: JournalExportAttachment,
  labels: JournalPdfLabels,
  locale: string,
  x: number,
  width: number,
  embeddedByDocumentId?: Readonly<Record<string, Uint8Array>>,
) {
  const number = attachment.bilagNumber;
  if (number == null) return;
  doc.gap(6);
  doc.flowLine(bilagTitle(number, labels), {
    size: 9,
    leading: 12,
    bold: true,
    x,
    maxWidth: width,
  });
  const embedded =
    attachment.documentId != null ? embeddedByDocumentId?.[attachment.documentId] : undefined;
  const meta = attachmentMetaLines(
    embedded ? { ...attachment, zipFilename: undefined } : attachment,
    labels,
    locale,
  );
  for (const line of meta) {
    drawWrapped(doc, wrapText(line, width, 9), {
      size: 9,
      leading: 12,
      x,
      maxWidth: width,
    });
  }
  if (embedded) {
    doc.drawJpeg(embedded, { x, maxWidth: width });
  }
}

function drawAmendment(
  doc: PdfDocument,
  amendment: JournalExportAmendment,
  labels: JournalPdfLabels,
  locale: string,
  includeBilag: boolean,
  embeddedByDocumentId?: Readonly<Record<string, Uint8Array>>,
) {
  const x = MARGIN + AMENDMENT_INDENT;
  const width = CONTENT_WIDTH - AMENDMENT_INDENT;
  const plain = journalMarkdownToPlainText(amendment.content);
  const content = plain ? wrapMultiline(plain, width, BODY_SIZE) : [];
  const headerHeight = META_LEADING * 3 + 10;
  doc.ensureSpace(headerHeight + BODY_LEADING);
  doc.gap(10);
  doc.flowLine(amendmentKindLabel(amendment.kind, labels), {
    size: 9,
    leading: META_LEADING,
    bold: true,
    x,
    maxWidth: width,
  });
  const when = formatDateTime(amendment.finalizedAt ?? amendment.createdAt, locale);
  const who = amendment.practitionerName;
  doc.flowLine(who ? `${when} · ${who}` : when, {
    size: 8,
    leading: 11,
    color: COLORS.muted,
    x,
    maxWidth: width,
  });
  if (amendment.status === 'draft') {
    doc.flowLine(labels.draft, {
      size: 8,
      leading: 11,
      color: COLORS.muted,
      x,
      maxWidth: width,
    });
  }
  doc.gap(4);
  if (content.length === 0) {
    doc.flowLine('—', {
      size: BODY_SIZE,
      leading: BODY_LEADING,
      color: COLORS.muted,
      x,
      maxWidth: width,
    });
  } else {
    drawWrapped(doc, content, { size: BODY_SIZE, leading: BODY_LEADING, x, maxWidth: width });
  }
  if (includeBilag) {
    for (const attachment of amendment.attachments) {
      drawBilagReference(doc, attachment, labels, locale, x, width, embeddedByDocumentId);
    }
  }
}

function drawEntry(
  doc: PdfDocument,
  entry: JournalExportEntry,
  labels: JournalPdfLabels,
  locale: string,
  includeBilag: boolean,
  embeddedByDocumentId?: Readonly<Record<string, Uint8Array>>,
) {
  const plain = journalMarkdownToPlainText(entry.content);
  const content = plain ? wrapMultiline(plain, CONTENT_WIDTH, BODY_SIZE) : [];
  doc.ensureSpace(DATE_LEADING + META_LEADING * 3 + BODY_LEADING + 16);
  doc.line(MARGIN, doc.y, MARGIN + CONTENT_WIDTH, doc.y, COLORS.line, 0.6);
  doc.gap(12);
  doc.flowLine(entryHeading(entry, locale), {
    size: DATE_SIZE,
    leading: DATE_LEADING,
    bold: true,
  });
  if (entry.serviceName) {
    doc.flowLine(entry.serviceName, { size: META_SIZE, leading: META_LEADING });
  }
  if (entry.practitionerName) {
    doc.flowLine(entry.practitionerName, { size: META_SIZE, leading: META_LEADING });
  }
  doc.flowLine(statusLabel(entry.status, labels), {
    size: 8,
    leading: 11,
    color: COLORS.muted,
  });
  if (entry.title && entry.title !== entry.serviceName) {
    doc.flowLine(entry.title, { size: 9, leading: META_LEADING, bold: true });
  }
  doc.gap(6);
  if (content.length === 0) {
    doc.flowLine('—', { size: BODY_SIZE, leading: BODY_LEADING, color: COLORS.muted });
  } else {
    drawWrapped(doc, content, { size: BODY_SIZE, leading: BODY_LEADING });
  }
  if (includeBilag) {
    for (const attachment of entry.attachments) {
      drawBilagReference(
        doc,
        attachment,
        labels,
        locale,
        MARGIN,
        CONTENT_WIDTH,
        embeddedByDocumentId,
      );
    }
  }
  for (const amendment of entry.amendments) {
    drawAmendment(doc, amendment, labels, locale, includeBilag, embeddedByDocumentId);
  }
  doc.gap(8);
}

function drawBilagCatalog(
  doc: PdfDocument,
  items: JournalBilagItem[],
  labels: JournalPdfLabels,
  locale: string,
  embeddedByDocumentId?: Readonly<Record<string, Uint8Array>>,
) {
  if (items.length === 0) return;
  doc.ensureSpace(DATE_LEADING + META_LEADING * 2);
  doc.line(MARGIN, doc.y, MARGIN + CONTENT_WIDTH, doc.y, COLORS.line, 0.6);
  doc.gap(12);
  doc.flowLine(labels.bilagSection, {
    size: DATE_SIZE,
    leading: DATE_LEADING,
    bold: true,
  });
  doc.gap(4);
  for (const item of items) {
    doc.ensureSpace(META_LEADING * 6);
    doc.gap(8);
    doc.flowLine(bilagTitle(item.number, labels), {
      size: 10,
      leading: 13,
      bold: true,
    });
    const embedded = item.documentId != null ? embeddedByDocumentId?.[item.documentId] : undefined;
    const attachment: JournalExportAttachment = {
      documentId: item.documentId,
      filename: item.filename,
      mimeType: item.mimeType,
      createdAt: item.createdAt,
      uploadedAt: item.uploadedAt,
      uploadedBy: item.uploadedBy,
      notes: item.notes,
      bilagNumber: item.number,
      zipFilename: embedded ? undefined : item.zipFilename,
      zipPath: embedded ? undefined : item.zipPath,
    };
    for (const line of attachmentMetaLines(attachment, labels, locale, {
      includeRelated: {
        date: item.relatedClinicalDate,
        service: item.relatedServiceName,
      },
    })) {
      drawWrapped(doc, wrapText(line, CONTENT_WIDTH, 9), {
        size: 9,
        leading: 12,
      });
    }
  }
}

export function journalPdfLabelsFromTranslator(t: (key: string) => string): JournalPdfLabels {
  return {
    brand: t('journal.pdf.brand'),
    title: t('journal.pdf.title'),
    patient: t('journal.pdf.patient'),
    patientId: t('journal.pdf.patient_id'),
    clinic: t('journal.pdf.clinic'),
    exported: t('journal.pdf.exported'),
    attachments: t('journal.pdf.attachments'),
    correction: t('journal.pdf.correction'),
    addendum: t('journal.pdf.addendum'),
    finalized: t('journal.filter_status_finalized'),
    draft: t('journal.filter_status_draft'),
    page: t('journal.pdf.page'),
    pageOf: t('journal.pdf.page_of'),
    footer: t('journal.pdf.footer'),
    empty: t('journal.pdf.empty'),
    bilag: t('journal.pdf.bilag'),
    bilagSection: t('journal.pdf.bilag_section'),
    originalFile: t('journal.pdf.original_file'),
    filename: t('journal.pdf.filename'),
    fileType: t('journal.pdf.file_type'),
    date: t('journal.pdf.date'),
    uploadedBy: t('journal.pdf.uploaded_by'),
    relatedEntry: t('journal.pdf.related_entry'),
  };
}

export function journalExportToPdf(
  document: JournalExportDocument,
  options: JournalPdfOptions = {},
): Uint8Array {
  const locale = options.locale ?? 'en-GB';
  const labels = options.labels ?? EN_JOURNAL_PDF_LABELS;
  const generatedAt = options.generatedAt ?? new Date(document.exportedAt);
  const includeBilag = options.includeBilag !== false;
  const { document: numbered, bilag } = assignJournalBilag(document);
  const doc = new PdfDocument();
  doc.footerLeft = `${labels.brand} · ${labels.footer}`;
  doc.footerRightPrefix = labels.page;
  doc.footerOf = labels.pageOf;

  const exportedLabel = Number.isNaN(generatedAt.getTime())
    ? numbered.exportedAt
    : formatDateTime(generatedAt.toISOString(), locale);

  const meta = [
    { label: labels.patient, value: numbered.patientName },
    { label: labels.patientId, value: numbered.patientNumber ?? '—' },
    ...(numbered.clinicName ? [{ label: labels.clinic, value: numbered.clinicName }] : []),
    { label: labels.exported, value: exportedLabel },
  ];

  drawReportHeader(doc, {
    brand: labels.brand,
    title: labels.title,
    meta,
  });

  if (numbered.entries.length === 0) {
    doc.flowLine(labels.empty, { size: BODY_SIZE, leading: BODY_LEADING, color: COLORS.muted });
    return doc.build();
  }

  for (const entry of numbered.entries) {
    drawEntry(doc, entry, labels, locale, includeBilag, options.embeddedByDocumentId);
  }
  if (includeBilag) {
    drawBilagCatalog(doc, bilag, labels, locale, options.embeddedByDocumentId);
  }
  return doc.build();
}

export function isPdfBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}
